import axios from "axios";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import { API_URL } from "../../../config";
import { AuthContext } from "../../../Context/AuthContext";
import { canDo, canView } from "../../../utils/acl";
import "./ProjectsDashboard.css";

const ProjectList = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [search, setSearch] = useState("");
  const [fetchError, setFetchError] = useState("");
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const clientNameById = useMemo(() => {
    const map = new Map();
    clients.forEach((c) => {
      if (c?._id) map.set(String(c._id), c.leadName || c.name || "");
    });
    return map;
  }, [clients]);

  const getClientLabel = (project) => {
    const c = project?.clientId;
    if (c && typeof c === "object") return c.leadName || c.clientName || "N/A";
    if (typeof c === "string") return clientNameById.get(c) || project?.clientName || "N/A";
    return project?.clientName || "N/A";
  };


  const getProjectRouteId = (project) =>
    project?._id || project?.id || "";

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const [projectRes, clientRes] = await Promise.allSettled([
          axios.get(`${API_URL}/api/getProjects`),
          axios.get(`${API_URL}/api/getClientData`),
        ]);

        const directProjects =
          projectRes.status === "fulfilled" && Array.isArray(projectRes.value.data)
            ? projectRes.value.data
            : [];

        const clientRows =
          clientRes.status === "fulfilled"
            ? Array.isArray(clientRes.value.data)
              ? clientRes.value.data
              : []
            : [];

        const linkedProjects = clientRows.flatMap((c) =>
          Array.isArray(c.projects)
            ? c.projects.map((p) => ({
                ...(p && typeof p === "object" ? p : { _id: String(p) }),
                clientId:
                  p && typeof p === "object" && p.clientId
                    ? p.clientId
                    : { _id: c._id, leadName: c.leadName },
                clientName: c.leadName || c.name || "N/A",
              }))
            : []
        );

        const merged = [...directProjects, ...linkedProjects];
        const deduped = Array.from(
          new Map(
            merged.map((p) => {
              const key = String(getProjectRouteId(p));
              return [key, p];
            })
          ).values()
        ).filter((p) => getProjectRouteId(p));

        setProjects(deduped);
        setFetchError("");
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects([]);
        setFetchError("Unable to load projects right now.");
      }
    };
    fetchProjects();

    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/getClientData`);
        const rows = Array.isArray(res.data) ? res.data : res.data?.clients || [];
        setClients(rows);
      } catch (err) {
        console.error("Error fetching clients:", err);
        setClients([]);
      }
    };
    fetchClients();
  }, []);

  const handleDelete = async (projectId, projectName) => {
    if (!canDo(user, "projects", "delete")) {
      return alert("You do not have permission to delete projects.");
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete project "${projectName}"?`
    );
    if (!confirmDelete) return;

    try {
      await axios.delete(`${API_URL}/api/deleteProjectById/${projectId}`);
      setProjects((prev) => prev.filter((proj) => proj._id !== projectId));
    } catch (err) {
      alert("Failed to delete project.");
    }
  };

  const handleView = (id) => {
    if (!canDo(user, "projects", "view")) {
      return alert("You do not have permission.");
    }
    if (!id) {
      return alert("This project has an invalid ID. Please refresh and try again.");
    }
    navigate(`/admin/getAllprojects/${id}`);
  };

  const handleAddProject = () => {
    if (!canDo(user, "projects", "add")) {
      return alert("You do not have permission to add projects.");
    }

    if (!selectedClientId) {
      return alert("Please select a client first.");
    }

    const selectedClient = clients.find((c) => c._id === selectedClientId);
    if (!selectedClient) {
      return alert("Selected client is invalid. Please choose again.");
    }

    navigate(`/admin/addProject/${selectedClient._id}`, {
      state: { client: selectedClient },
    });
  };

  if (!canView(user, "projects")) {
    return <div className="text-center mt-5 text-danger">Access Denied</div>;
  }

  const exportExcel = () => {
    const rows = filteredProjects.map((proj) => ({
      ID: proj.projectId || "N/A",
      Name: proj.projectName || "N/A",
      Client: getClientLabel(proj),
      Start: proj.startDate ? new Date(proj.startDate).toLocaleDateString() : "N/A",
      End: proj.endDate ? new Date(proj.endDate).toLocaleDateString() : "N/A",
      Members: proj.addMember?.length || 0,
      Budget: Number(proj.price || 0),
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Projects");
    XLSX.writeFile(workbook, "Projects_List.xlsx");
  };

  const totalBudget = projects.reduce((sum, p) => sum + (Number(p.price) || 0), 0);
  const totalClients = new Set(
    projects
      .map((p) => getClientLabel(p))
      .filter(Boolean)
  ).size;

  const activeProjects = projects.filter((p) => {
    if (!p.endDate) return false;
    return new Date(p.endDate) >= new Date();
  }).length;

  const filteredProjects = projects.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;

    const fields = [
      p.projectId,
      p.projectName,
      getClientLabel(p),
      p.department?.deptName,
      p.service?.serviceName,
      String(p.price ?? ""),
    ];

    return fields.some((f) => String(f || "").toLowerCase().includes(q));
  });

  return (
    <div className="projects-dash-page">
      <div className="projects-shell">
        <div className="projects-hero">
          <div>
            <p className="projects-eyebrow">Delivery and Execution</p>
            <h4 className="mb-1">Projects Dashboard</h4>
            <span>Track delivery, budget distribution, and client mapping</span>
          </div>
          <button onClick={exportExcel} className="projects-export-btn">
            Export Excel
          </button>
        </div>

        <div className="projects-kpi-grid">
          <div className="projects-kpi-card projects-kpi-one">
            <p>Total Projects</p>
            <h3>{projects.length}</h3>
          </div>

          <div className="projects-kpi-card projects-kpi-two">
            <p>Total Budget</p>
            <h3>Rs {totalBudget.toLocaleString()}</h3>
          </div>

          <div className="projects-kpi-card projects-kpi-three">
            <p>Clients</p>
            <h3>{totalClients}</h3>
          </div>

          <div className="projects-kpi-card projects-kpi-four">
            <p>Active</p>
            <h3>{activeProjects}</h3>
          </div>
        </div>

        <div className="projects-search-card">
          <input
            type="text"
            placeholder="Search by project id, name, client, dept, service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="projects-search-input"
          />
          {canDo(user, "projects", "add") && (
            <>
              <select
                className="projects-client-select"
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
              >
                <option value="">Select Client</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>
                    {(c.leadName || c.name || "Client")} {c.leadId ? `(${c.leadId})` : ""}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="projects-add-btn"
                onClick={handleAddProject}
              >
                Add Project
              </button>
            </>
          )}
          {search && (
            <button
              type="button"
              className="projects-clear-btn"
              onClick={() => setSearch("")}
            >
              Clear
            </button>
          )}
        </div>

        <div className="projects-table-card">
          {fetchError ? <div className="projects-fetch-error">{fetchError}</div> : null}
          <div className="table-responsive">
            <table className="table text-center align-middle mb-0">
              <thead>
                <tr>
                  {["ID", "Name", "Client", "Start", "End", "Members", "Budget", "Actions"].map(
                    (col, i) => (
                      <th key={i}>{col}</th>
                    )
                  )}
                </tr>
              </thead>

              <tbody>
                {filteredProjects.length > 0 ? (
                  filteredProjects.map((p) => (
                    <tr key={getProjectRouteId(p)}>
                      <td>{p.projectId}</td>
                      <td className="fw-semibold">{p.projectName}</td>
                      <td>{getClientLabel(p)}</td>
                      <td>{p.startDate ? new Date(p.startDate).toLocaleDateString() : "N/A"}</td>
                      <td>{p.endDate ? new Date(p.endDate).toLocaleDateString() : "N/A"}</td>
                      <td>{p.addMember?.length || 0}</td>
                      <td>Rs {Number(p.price || 0).toLocaleString()}</td>

                      <td>
                        {canDo(user, "projects", "view") && (
                          <button
                            className="btn btn-sm projects-act-btn view me-2"
                            onClick={() => handleView(getProjectRouteId(p))}
                          >
                            View
                          </button>
                        )}
                        {canDo(user, "projects", "delete") && (
                          <button
                            className="btn btn-sm projects-act-btn delete"
                            onClick={() => handleDelete(p._id, p.projectName)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="projects-empty-row">
                      No projects found for the current search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectList;
